"use client"

import React, { useMemo, useEffect, useRef } from "react"
import { ScrollView, StyleSheet, View, TouchableOpacity } from "react-native"
import { Text, useTheme } from "react-native-paper"
import { TreeItem } from "./TreeItem"
import type { TreeNode } from "./types"

interface TreeViewProps {
  data: TreeNode
  onSelect: (node: TreeNode, selectChildren: boolean) => void
  selectedNodes: string[]
  externalSelectedNode?: TreeNode | null // Add this prop to handle selection from map
}

interface EnhancedTreeNode extends TreeNode {
  parent?: EnhancedTreeNode
}

export const TreeView: React.FC<TreeViewProps> = ({ data, onSelect, selectedNodes, externalSelectedNode }) => {
  const theme = useTheme()
  // Add a ref to track if selection is coming from the tree
  const selectionFromTreeRef = useRef<boolean>(false)

  // Build tree with parent references
  const enhancedTree = useMemo(() => {
    const enhanceNode = (node: TreeNode, parent?: EnhancedTreeNode): EnhancedTreeNode => {
      const enhanced = { ...node, parent } as EnhancedTreeNode
      if (node.children) {
        enhanced.children = node.children.map((child) => enhanceNode(child, enhanced))
      }
      return enhanced
    }
    return enhanceNode(data)
  }, [data])

  // Auto-select root node on first load
  useEffect(() => {
    // Only select root node if no nodes are currently selected
    if (selectedNodes.length === 0 && data) {
      // Determine if we should select children based on node type
      const selectChildren = data.type === "line" || data.type === "section"

      // Call onSelect with the root node
      onSelect(data, selectChildren)

      // Auto-selected root node
    }
  }, [data, onSelect, selectedNodes])

  // Handle external selection from map
  useEffect(() => {
    // Skip if selection is coming from the tree to prevent recursion
    if (selectionFromTreeRef.current) {
      selectionFromTreeRef.current = false
      return
    }

    if (externalSelectedNode) {
      // Find the node in the tree
      const findNode = (node: EnhancedTreeNode, targetName: string): EnhancedTreeNode | null => {
        if (node.name === targetName) {
          return node
        }

        if (node.children) {
          for (const child of node.children) {
            const found = findNode(child as EnhancedTreeNode, targetName)
            if (found) return found
          }
        }

        return null
      }

      const foundNode = findNode(enhancedTree, externalSelectedNode.name)

      if (foundNode) {
        // Determine if we should select children based on node type
        const selectChildren = foundNode.type === "line" || foundNode.type === "section"

        // Call onSelect with the found node
        onSelect(foundNode, selectChildren)
      }
    }
  }, [externalSelectedNode, enhancedTree, onSelect])

  // Function to get node hierarchy path
  const getNodePath = (node: EnhancedTreeNode | undefined): EnhancedTreeNode[] => {
    const path: EnhancedTreeNode[] = []
    let currentNode = node

    while (currentNode) {
      path.unshift(currentNode)
      currentNode = currentNode.parent
    }

    return path
  }

  // Find the currently selected node
  const findSelectedNode = (root: EnhancedTreeNode): EnhancedTreeNode | undefined => {
    if (selectedNodes.includes(root.name)) {
      return root
    }
    if (root.children) {
      for (const child of root.children) {
        const found = findSelectedNode(child as EnhancedTreeNode)
        if (found) return found
      }
    }
    return undefined
  }

  const selectedNode = findSelectedNode(enhancedTree)

  // Modified onSelect handler to set the flag
  const handleSelect = (node: TreeNode, selectChildren: boolean) => {
    // Set flag to prevent recursion
    selectionFromTreeRef.current = true
    onSelect(node, selectChildren)
  }

  return (
    <View style={styles.container}>
      {selectedNode && (
        <View style={[styles.breadcrumb, { borderBottomColor: theme.colors.outline }]}>
          {getNodePath(selectedNode).map((node, index, array) => (
            <React.Fragment key={node.name}>
              <TouchableOpacity
                onPress={() => {
                  // Use same logic as TreeItem for selecting children
                  const selectChildren = node.type === "line" || node.type === "section"
                  handleSelect(node, selectChildren)
                }}
                style={styles.breadcrumbItem}
              >
                <Text style={[styles.breadcrumbText, { color: theme.colors.primary }]}>{node.name}</Text>
              </TouchableOpacity>
              {index < array.length - 1 && (
                <Text style={[styles.breadcrumbSeparator, { color: theme.colors.outline }]}>/</Text>
              )}
            </React.Fragment>
          ))}
        </View>
      )}
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true} showsHorizontalScrollIndicator={false}>
        <TreeItem
          node={enhancedTree}
          level={0}
          onSelect={handleSelect}
          isSelected={selectedNodes.includes(enhancedTree.name)}
          selectedNodes={selectedNodes}
        />
      </ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  scrollView: {
    flex: 1,
    overflow: "hidden",
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    borderBottomWidth: 1,
    flexWrap: "wrap",
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbText: {
    fontSize: 12,
  },
  breadcrumbSeparator: {
    marginHorizontal: 4,
    fontSize: 12,
  },
})

